import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/brand-mark";
import { usePageTitle } from "@/hooks/use-page-title";

/**
 * Public Privacy Policy and Terms of Service.
 *
 * Both pages are reachable without signing in: Google's OAuth brand
 * verification opens them from the consent screen, and the signup and login
 * pages link to them. Keep the text in step with what CompBox actually does —
 * if a new third-party processor or a new kind of personal data is added,
 * update the Privacy Policy and bump LEGAL_EFFECTIVE_DATE.
 */

export const LEGAL_EFFECTIVE_DATE = "October 21, 2026";
export const LEGAL_ENTITY = "PaisleyTech LLC";
export const LEGAL_CONTACT_EMAIL = "support@mycompbox.com";

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  usePageTitle(title);
  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-3xl">
        <Link to="/" aria-label="CompBox home">
          <BrandMark />
        </Link>
        <h1 className="mt-10 text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Effective {LEGAL_EFFECTIVE_DATE}</p>
        <div className="mt-8 space-y-8 text-sm leading-relaxed text-foreground/90">{children}</div>
        <LegalFooter />
      </div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">{heading}</h2>
      {children}
    </section>
  );
}

function List({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function ContactEmail() {
  return (
    <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-primary hover:underline">
      {LEGAL_CONTACT_EMAIL}
    </a>
  );
}

/** Small "Terms · Privacy" link row, shared by the legal pages and the auth screens. */
export function LegalLinks({ className }: { className?: string }) {
  return (
    <span className={className}>
      <Link to="/terms" className="hover:underline">
        Terms of Service
      </Link>
      {" · "}
      <Link to="/privacy" className="hover:underline">
        Privacy Policy
      </Link>
    </span>
  );
}

function LegalFooter() {
  return (
    <footer className="mt-16 border-t border-border pt-6 text-xs text-muted-foreground">
      <p>
        CompBox is a product of {LEGAL_ENTITY}. <LegalLinks />
      </p>
    </footer>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This Privacy Policy explains how {LEGAL_ENTITY} (&quot;we&quot;, &quot;us&quot;) collects,
        uses and shares information when you use CompBox, including the website at mycompbox.com,
        the CompBox command-line tool, the CompBox MCP server and the machine agent (together, the
        &quot;Service&quot;). It applies only to CompBox, not to other {LEGAL_ENTITY} products.
      </p>

      <Section heading="Information we collect">
        <p>
          <strong>Account information.</strong> Your name, email address and a one-way hash of your
          password. If you sign in with Google or Microsoft, we receive your name, email address,
          whether that email is verified, and the account identifier the provider assigns to you.
          We do not receive your Google or Microsoft password.
        </p>
        <p>
          <strong>Content you add.</strong> Everything you and your organization put into CompBox:
          epics, features, tasks, requests, comments, decisions, notes, uploaded images, and links
          to code files, branches, commits and pull requests. AI agents you connect to CompBox may
          also write progress notes and context on your behalf.
        </p>
        <p>
          <strong>Organization information.</strong> Your organization name, its members, their
          roles, teams and pending invitations.
        </p>
        <p>
          <strong>Machine and agent information.</strong> If you register a computer to run CompBox
          jobs, we store the name you give it, a hashed access token, when it was last seen, the
          status of the AI runtime on it, and the code repositories it is set up to work on.
        </p>
        <p>
          <strong>Connected services.</strong> If an administrator connects GitHub, we store the
          GitHub installation identifier and the names of the accounts and repositories it covers.
          If an administrator connects a cloud account such as Google Cloud, we store only its
          identifiers: the project ID, region, the name of the deploy service account and the
          identity pool CompBox created. We never store cloud passwords, keys or access tokens.
        </p>
        <p>
          <strong>Usage and technical information.</strong> Activity within the Service (for
          example, which items were created or changed and when), and standard server logs such as
          IP address, browser type and request times, used for security and troubleshooting.
        </p>
        <p>
          <strong>Billing information.</strong> If your organization buys a paid plan, payments are
          handled by Stripe. We receive the subscription status and seat count, but never your full
          card number.
        </p>
      </Section>

      <Section heading="How we use information">
        <List
          items={[
            "To provide, maintain and secure the Service, including signing you in and keeping your organization's data separate from other organizations.",
            "To send service emails, such as account verification, invitations and important notices about your account. We do not send marketing email without your consent.",
            "To provide support and respond to your requests.",
            "To detect and prevent abuse, fraud and security incidents.",
            "To understand how the Service is used so we can fix problems and improve it.",
          ]}
        />
        <p>
          We do not sell your personal information, we do not use it for advertising, and we do not
          use your content to train AI models.
        </p>
      </Section>

      <Section heading="AI agents">
        <p>
          CompBox coordinates AI coding agents (such as Claude Code) that run on computers you or
          your organization control, using your own AI provider account or API key. When an agent
          works on a CompBox task, the task details and related content are sent to that AI provider
          from your computer, under your agreement with that provider. We are not responsible for
          how third-party AI providers handle data you send them.
        </p>
      </Section>

      <Section heading="Information from Google">
        <p>
          When you choose &quot;Continue with Google&quot;, we request only your basic profile (name,
          email address and account identifier) to create and sign in to your CompBox account. We use
          this information only for that purpose and do not share it with third parties except the
          service providers listed below that host the Service.
        </p>
        <p>
          When an organization administrator chooses &quot;Connect Google Cloud&quot;, we ask for
          one-time access to the Google Cloud project they name (the{" "}
          <code className="text-xs">cloud-platform</code> scope). We use that access only to set up
          deployments in that project, once: we turn on the Google Cloud services deployments need
          (Cloud Run, Artifact Registry, IAM, Cloud Storage), create a deploy service account and
          give it the roles to deploy, create a Workload Identity pool that trusts only your
          organization&apos;s GitHub accounts, and create a storage bucket for deployment state. We
          do not read, copy or change anything else in your Google account or project. While setup
          runs, the access token is kept only in encrypted form; as soon as setup finishes or
          fails, we revoke it with Google and discard it. We keep only the identifiers listed under
          &quot;Connected services&quot;. Later deployments run from GitHub Actions in your own
          repositories, which sign in to Google Cloud directly and do not pass through CompBox.
          You can remove the connection in CompBox at any time, and remove CompBox&apos;s access
          entirely by deleting the deploy service account and identity pool in your project.
        </p>
        <p>
          We do not sell information received from Google APIs, use it for advertising, or use it
          to train AI models, and no person at {LEGAL_ENTITY} reads it except with your consent,
          for security or legal reasons. CompBox&apos;s use and transfer of
          information received from Google APIs adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="text-primary hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </Section>

      <Section heading="How information is shared">
        <p>
          <strong>Within your organization.</strong> Content you add is visible to members of your
          organization according to their team access. Organization administrators can see the
          organization&apos;s members and manage their access.
        </p>
        <p>
          <strong>Service providers.</strong> We use the following providers to run the Service.
          They process information only on our instructions:
        </p>
        <List
          items={[
            "Google Cloud and Firebase — application hosting, website hosting and file storage.",
            "Aiven — managed database hosting.",
            "Resend — delivery of service emails.",
            "Stripe — payment processing, for paid plans.",
            "Google and Microsoft — sign-in, if you choose to use them.",
            "GitHub — repository access and pull requests, if your organization connects it.",
          ]}
        />
        <p>
          <strong>Legal reasons.</strong> We may disclose information if required by law, or to
          protect the rights, safety or property of our users, the public or {LEGAL_ENTITY}.
        </p>
        <p>
          <strong>Business transfers.</strong> If CompBox or {LEGAL_ENTITY} is involved in a merger,
          acquisition or sale of assets, information may be transferred as part of that transaction.
          We will notify you before your information becomes subject to a different privacy policy.
        </p>
      </Section>

      <Section heading="Cookies and local storage">
        <p>
          CompBox uses your browser&apos;s local storage to keep you signed in and to remember
          preferences such as your theme. We do not use advertising cookies or third-party
          analytics trackers.
        </p>
      </Section>

      <Section heading="Data retention">
        <p>
          We keep your information for as long as your account or your organization&apos;s
          account is active. When an account is deleted, we delete or anonymize its personal
          information within a reasonable period, except where we must keep it to meet legal
          obligations, resolve disputes or enforce our agreements. Copies in backups are removed as
          those backups expire.
        </p>
      </Section>

      <Section heading="Security">
        <p>
          We protect information with measures such as encryption in transit, hashed passwords and
          access tokens, and per-organization access controls. No system is perfectly secure, so we
          cannot guarantee absolute security.
        </p>
      </Section>

      <Section heading="Your choices and rights">
        <p>
          You can view and update your account details in CompBox. You can ask us to access,
          correct, export or delete your personal information by emailing <ContactEmail />.
          Depending on where you live, you may have additional rights under laws such as the GDPR or
          the California Consumer Privacy Act, and you may complain to your local data protection
          authority. We will not discriminate against you for exercising these rights.
        </p>
        <p>
          If your account belongs to an organization, some requests may need to go through your
          organization&apos;s administrator, who controls the organization&apos;s content.
        </p>
      </Section>

      <Section heading="International transfers">
        <p>
          CompBox is operated from the United States, and information is stored and processed in
          the United States. By using the Service you understand that your information will be
          transferred there.
        </p>
      </Section>

      <Section heading="Children">
        <p>
          The Service is meant for professional use and is not directed to children under 16. We do
          not knowingly collect personal information from children under 16.
        </p>
      </Section>

      <Section heading="Changes to this policy">
        <p>
          We may update this policy from time to time. If we make material changes, we will notify
          you by email or within the Service before they take effect. The effective date at the top
          shows when this policy last changed.
        </p>
      </Section>

      <Section heading="Contact">
        <p>
          Questions about this policy or your information: <ContactEmail />, {LEGAL_ENTITY}.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service">
      <p>
        These Terms of Service (&quot;Terms&quot;) are an agreement between you and {LEGAL_ENTITY}{" "}
        (&quot;we&quot;, &quot;us&quot;) and govern your use of CompBox, including the website at
        mycompbox.com, the CompBox command-line tool, the CompBox MCP server and the machine agent
        (together, the &quot;Service&quot;). By creating an account or using the Service, you agree
        to these Terms. If you use the Service on behalf of an organization, you agree on its behalf
        and confirm you have authority to do so.
      </p>

      <Section heading="Your account">
        <List
          items={[
            "You must give accurate information when you sign up and keep it up to date.",
            "You are responsible for keeping your password, API tokens and machine tokens secure, and for activity under your account.",
            "Tell us promptly at the contact address below if you believe your account has been compromised.",
            "You must be at least 16 years old to use the Service.",
          ]}
        />
      </Section>

      <Section heading="Organizations">
        <p>
          When you create an account you may create an organization or join one. Organization
          administrators can invite and remove members, manage teams and control access to the
          organization&apos;s content. If you join an organization, its administrators may be able
          to see and manage your activity and the content you add there.
        </p>
      </Section>

      <Section heading="Your content">
        <p>
          You and your organization keep ownership of the content you put into CompBox. You grant
          us a limited license to host, store, copy, process and display that content only as
          needed to provide and secure the Service. You are responsible for your content and for
          having the rights needed to put it into the Service.
        </p>
      </Section>

      <Section heading="Acceptable use">
        <p>You agree not to:</p>
        <List
          items={[
            "break the law or infringe anyone's rights using the Service;",
            "upload malware, or try to gain unauthorized access to the Service, other accounts or our systems;",
            "interfere with or overload the Service, or get around its rate limits or security controls;",
            "resell or provide the Service to third parties except as we allow;",
            "use the Service to build a competing product by copying its features or content.",
          ]}
        />
      </Section>

      <Section heading="AI agents and third-party services">
        <p>
          The Service can direct AI coding agents that run on computers you control and can connect
          to third-party services such as GitHub, Google and Microsoft. Your use of those agents
          and services is governed by your agreements with their providers. You are responsible for
          reviewing changes AI agents make to your code before you rely on them; AI output can be
          wrong or incomplete. We are not responsible for third-party services or their output.
        </p>
        <p>
          If you connect a cloud account, deployments run in that account and its provider bills
          you directly for what they use. You are responsible for those charges and for what you
          choose to deploy.
        </p>
      </Section>

      <Section heading="Fees">
        <p>
          Some features may require a paid plan. Prices and billing terms are shown before you
          subscribe. Paid plans renew automatically until cancelled, and payments are processed by
          Stripe. Except where required by law, fees are non-refundable. We will give you advance
          notice of price changes.
        </p>
      </Section>

      <Section heading="Changes to the Service">
        <p>
          We are continually improving CompBox and may add, change or remove features. If we make a
          change that materially reduces the Service you have paid for, we will tell you in advance.
        </p>
      </Section>

      <Section heading="Suspension and termination">
        <p>
          You can stop using the Service at any time. We may suspend or terminate access if you
          materially breach these Terms, if required by law, or to protect the Service or other
          users. Where reasonable, we will give notice first. After termination, you can ask us for
          an export of your organization&apos;s content within 30 days, after which we may delete
          it.
        </p>
      </Section>

      <Section heading="Disclaimers">
        <p className="uppercase">
          The Service is provided &quot;as is&quot; and &quot;as available&quot;. To the fullest
          extent permitted by law, we disclaim all warranties, express or implied, including
          warranties of merchantability, fitness for a particular purpose and non-infringement. We
          do not warrant that the Service will be uninterrupted, error-free or secure.
        </p>
      </Section>

      <Section heading="Limitation of liability">
        <p className="uppercase">
          To the fullest extent permitted by law, {LEGAL_ENTITY} will not be liable for any
          indirect, incidental, special, consequential or punitive damages, or for lost profits,
          revenue or data. Our total liability for any claim relating to the Service is limited to
          the greater of the amount you paid us for the Service in the 12 months before the claim
          and US$100.
        </p>
      </Section>

      <Section heading="Indemnity">
        <p>
          You will defend and indemnify {LEGAL_ENTITY} against claims arising from your content or
          your breach of these Terms, and pay resulting damages and reasonable costs.
        </p>
      </Section>

      <Section heading="Governing law">
        <p>
          These Terms are governed by the laws of the State of California and the federal laws of
          the United States, without regard to conflict-of-law rules. Disputes will be resolved in
          the state or federal courts located in California, unless the law where you live gives
          you the right to bring a claim elsewhere.
        </p>
      </Section>

      <Section heading="Changes to these Terms">
        <p>
          We may update these Terms. If we make material changes, we will notify you by email or
          within the Service before they take effect. Continuing to use the Service after that means
          you accept the updated Terms.
        </p>
      </Section>

      <Section heading="Privacy">
        <p>
          Our{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>{" "}
          explains how we handle personal information.
        </p>
      </Section>

      <Section heading="Contact">
        <p>
          Questions about these Terms: <ContactEmail />, {LEGAL_ENTITY}.
        </p>
      </Section>
    </LegalLayout>
  );
}
